import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { extractErrorMessage } from '../utils/errorUtils';
import { useConfirm } from './common/components';
import { FirstTimeUser, CompanyList, CreateCompany } from './company/components';
import { companyService } from '../services/companyService';
import type { Company } from '../services/companyService';

export default function CompaniesPage() {
  const { confirm, confirmDialog } = useConfirm();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchCompanies = async () => {
      try {
        const result = await companyService.getAll(true);
        if (cancelled) {
          return;
        }
        setCompanies(result);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Error fetching companies:', err);
        setError(extractErrorMessage(err, 'Не вдалося завантажити список компаній. Спробуйте ще раз.'));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCompanies();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleCompanies = showOnlyActive
    ? companies.filter((company) => !company.deactivatedDate)
    : companies;

  const handleCreateCompany = () => {
    setShowCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
  };

  const handleCompanyCreated = (newCompany: Company) => {
    setCompanies(prev => [...prev, newCompany]);
    setShowCreateDialog(false);
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!(await confirm('Ви впевнені, що хочете архівувати цю компанію?'))) {
      return;
    }

    await companyService.delete(companyId);
    setCompanies(prev => prev.map(c => (
      c.id === companyId ? { ...c, deactivatedDate: new Date().toISOString() } : c
    )));
  };

  const handleRestoreCompany = async (companyId: number) => {
    await companyService.restore(companyId);
    setCompanies(prev => prev.map(c => (
      c.id === companyId ? { ...c, deactivatedDate: null } : c
    )));
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (companies.length === 0) {
    return (
      <>
        <FirstTimeUser onCreateCompany={handleCreateCompany} />
        <CreateCompany
          open={showCreateDialog}
          onClose={handleCloseCreateDialog}
          onCompanyCreated={handleCompanyCreated}
        />
      </>
    );
  }

  return (
    <>
      <CompanyList
        companies={visibleCompanies}
        onCreateCompany={handleCreateCompany}
        onDeleteCompany={handleDeleteCompany}
        onRestoreCompany={handleRestoreCompany}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(prev => !prev)}
        showOnlyActive={showOnlyActive}
        onShowOnlyActiveChange={setShowOnlyActive}
      />
      {confirmDialog}
      <CreateCompany
        open={showCreateDialog}
        onClose={handleCloseCreateDialog}
        onCompanyCreated={handleCompanyCreated}
      />
    </>
  );
}
